// Top-level router with code-splitting via React.lazy. Each route loads on
// navigation, keeping the initial bundle tight. Suspense fallbacks render a
// minimal skeleton during lazy load (typically <50ms on a warm cache).

import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import AppShell from "@/components/shell/app-shell";
import { Skeleton } from "@/components/ui/skeleton";

// Eagerly loaded — first paint
import InboxRoute from "@/routes/inbox";
import HomeRoute from "@/routes/home";

// Lazy-loaded modules
const ContactsRoute = lazy(() => import("@/routes/contacts"));
const CompaniesRoute = lazy(() => import("@/routes/companies"));
const DealsRoute = lazy(() => import("@/routes/deals"));
const ContractsRoute = lazy(() => import("@/routes/contracts"));
const ContractDetailRoute = lazy(() => import("@/routes/contracts.$id"));
const ContractTemplatesRoute = lazy(() => import("@/routes/contracts.templates"));
const ContractTemplateDetailRoute = lazy(() => import("@/routes/contracts.templates.$id"));
const ContractClausesRoute = lazy(() => import("@/routes/contracts.clauses"));
const SowBuilderRoute = lazy(() => import("@/routes/sow.$id"));
const SequenceDetailRoute = lazy(() => import("@/routes/sequences.$id"));
const SequencesQueueRoute = lazy(() => import("@/routes/sequences.queue"));
const OnboardingPackRoute = lazy(() => import("@/routes/onboarding-pack.$id"));
const WorkflowsRoute = lazy(() => import("@/routes/workflows"));
const WorkflowDetailRoute = lazy(() => import("@/routes/workflows.$id"));
const ListsRoute = lazy(() => import("@/routes/lists"));
const ListDetailRoute = lazy(() => import("@/routes/lists.$id"));
const SequencesRoute = lazy(() => import("@/routes/sequences"));
const CampaignsRoute = lazy(() => import("@/routes/campaigns"));
const TemplatesRoute = lazy(() => import("@/routes/templates"));
const TasksRoute = lazy(() => import("@/routes/tasks"));
const ProjectsRoute = lazy(() => import("@/routes/projects"));
const ProjectDetailRoute = lazy(() => import("@/routes/projects.$slug"));
const ReportsRoute = lazy(() => import("@/routes/reports"));
const FormsRoute = lazy(() => import("@/routes/forms"));
const FormDetailRoute = lazy(() => import("@/routes/forms.$id"));
const CalculatorRoute = lazy(() => import("@/routes/calculator"));

// Per-user settings
const EmailSettingsRoute = lazy(() => import("@/routes/settings/email"));

// Admin (lazy, also code-split per sub-route)
const AdminLayout = lazy(() => import("@/routes/admin"));
const UsersAdmin = lazy(() => import("@/routes/admin/users"));
const IntegrationsAdmin = lazy(() => import("@/routes/admin/integrations"));
const CostsAdmin = lazy(() => import("@/routes/admin/costs"));
const AiAdmin = lazy(() => import("@/routes/admin/ai"));
const ApiKeysAdmin = lazy(() => import("@/routes/admin/api-keys"));
const BrandingAdmin = lazy(() => import("@/routes/admin/branding"));
const DiagnoseAdmin = lazy(() => import("@/routes/admin/diagnose"));
const AuditAdmin = lazy(() => import("@/routes/admin/audit"));
const ExpiryAdmin = lazy(() => import("@/routes/admin/expiry"));
const SuppressionsAdmin = lazy(() => import("@/routes/admin/suppressions"));
const PrivacyAdmin = lazy(() => import("@/routes/admin/privacy"));
const VariablesAdmin = lazy(() => import("@/routes/admin/variables"));
const RecycleBinAdmin = lazy(() => import("@/routes/admin/recycle-bin"));
const NotificationsAdmin = lazy(() => import("@/routes/admin/notifications"));
const TemplatesAdmin = lazy(() => import("@/routes/admin/templates"));
const ErrorLogAdmin = lazy(() => import("@/routes/admin/error-log"));

function RouteFallback() {
  return (
    <div className="flex h-full flex-col gap-3 p-6">
      <Skeleton className="h-8 w-1/3" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}

const lazy_ = (Comp: React.ComponentType) => (
  <Suspense fallback={<RouteFallback />}>
    <Comp />
  </Suspense>
);

export default function App() {
  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<HomeRoute />} />

        <Route path="inbox" element={<InboxRoute />} />
        <Route path="inbox/:conversationId" element={<InboxRoute />} />

        <Route path="contacts" element={lazy_(ContactsRoute)} />
        <Route path="contacts/:id" element={lazy_(ContactsRoute)} />

        <Route path="companies" element={lazy_(CompaniesRoute)} />
        <Route path="companies/:id" element={lazy_(CompaniesRoute)} />

        <Route path="deals" element={lazy_(DealsRoute)} />

        <Route path="contracts" element={lazy_(ContractsRoute)} />
        <Route path="contracts/templates" element={lazy_(ContractTemplatesRoute)} />
        <Route path="contracts/templates/:id" element={lazy_(ContractTemplateDetailRoute)} />
        <Route path="contracts/clauses" element={lazy_(ContractClausesRoute)} />
        <Route path="contracts/:id" element={lazy_(ContractDetailRoute)} />
        <Route path="onboarding-pack/:id" element={lazy_(OnboardingPackRoute)} />
        <Route path="sow/:id" element={lazy_(SowBuilderRoute)} />

        <Route path="sequences" element={lazy_(SequencesRoute)} />
        <Route path="sequences/queue" element={lazy_(SequencesQueueRoute)} />
        <Route path="sequences/:id" element={lazy_(SequenceDetailRoute)} />

        <Route path="workflows" element={lazy_(WorkflowsRoute)} />
        <Route path="workflows/:id" element={lazy_(WorkflowDetailRoute)} />
        <Route path="lists" element={lazy_(ListsRoute)} />
        <Route path="lists/:id" element={lazy_(ListDetailRoute)} />
        <Route path="campaigns" element={lazy_(CampaignsRoute)} />
        <Route path="templates" element={lazy_(TemplatesRoute)} />

        <Route path="tasks" element={lazy_(TasksRoute)} />
        <Route path="projects" element={lazy_(ProjectsRoute)} />
        <Route path="projects/:slug" element={lazy_(ProjectDetailRoute)} />
        <Route path="reports" element={lazy_(ReportsRoute)} />
        <Route path="forms" element={lazy_(FormsRoute)} />
        <Route path="forms/:id" element={lazy_(FormDetailRoute)} />

        <Route path="deals/calculator" element={lazy_(CalculatorRoute)} />

        <Route path="settings" element={<Navigate to="/settings/email" replace />} />
        <Route path="settings/email" element={lazy_(EmailSettingsRoute)} />

        <Route path="admin" element={lazy_(AdminLayout)}>
          <Route index element={<Navigate to="/admin/users" replace />} />
          <Route path="users" element={lazy_(UsersAdmin)} />
          <Route path="integrations" element={lazy_(IntegrationsAdmin)} />
          <Route path="costs" element={lazy_(CostsAdmin)} />
          <Route path="ai" element={lazy_(AiAdmin)} />
          <Route path="api-keys" element={lazy_(ApiKeysAdmin)} />
          <Route path="branding" element={lazy_(BrandingAdmin)} />
          <Route path="diagnose" element={lazy_(DiagnoseAdmin)} />
          <Route path="audit" element={lazy_(AuditAdmin)} />
          <Route path="expiry" element={lazy_(ExpiryAdmin)} />
          <Route path="suppressions" element={lazy_(SuppressionsAdmin)} />
          <Route path="privacy" element={lazy_(PrivacyAdmin)} />
          <Route path="variables" element={lazy_(VariablesAdmin)} />
          <Route path="recycle-bin" element={lazy_(RecycleBinAdmin)} />
          <Route path="notifications" element={lazy_(NotificationsAdmin)} />
          <Route path="templates/email" element={lazy_(TemplatesAdmin)} />
          <Route path="error-log" element={lazy_(ErrorLogAdmin)} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function NotFound() {
  return (
    <div className="flex h-full items-center justify-center text-ink-muted">
      <div className="text-center">
        <p className="font-display text-6xl font-bold text-ink">404</p>
        <p className="mt-2">Not built yet — this module is coming next.</p>
      </div>
    </div>
  );
}
